import * as s from './Skeleton.css';

interface SkeletonLineProps {
  width?: string;
  height?: string;
  className?: string;
}

export function SkeletonLine({ width = '100%', height = '12px', className }: SkeletonLineProps) {
  return (
    <div className={`${s.line} ${className ?? ''}`} style={{ width, height }} aria-hidden="true" />
  );
}

interface SkeletonBlockProps {
  width?: string;
  height?: string;
  borderRadius?: string;
  className?: string;
}

export function SkeletonBlock({
  width = '100%',
  height = '48px',
  borderRadius,
  className,
}: SkeletonBlockProps) {
  return (
    <div
      className={`${s.block} ${className ?? ''}`}
      style={{ width, height, borderRadius }}
      aria-hidden="true"
    />
  );
}

// --- Composite skeletons ---

interface TableSkeletonProps {
  rows?: number;
  cols?: number;
  label?: string;
}

export function TableSkeleton({
  rows = 5,
  cols = 4,
  label = '테이블 로딩 중',
}: TableSkeletonProps) {
  return (
    <div
      role="status"
      aria-busy="true"
      aria-label={label}
      style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 24 }}
    >
      {/* Header row */}
      <div style={{ display: 'flex', gap: 12 }}>
        {Array.from({ length: cols }, (_, i) => (
          <SkeletonBlock key={`h-${i}`} height="14px" />
        ))}
      </div>
      {/* Body rows */}
      {Array.from({ length: rows }, (_, r) => (
        <div key={`r-${r}`} style={{ display: 'flex', gap: 12 }}>
          {Array.from({ length: cols }, (_, c) => (
            <SkeletonBlock key={`r-${r}-c-${c}`} height="12px" />
          ))}
        </div>
      ))}
    </div>
  );
}

interface ListSkeletonProps {
  count?: number;
  label?: string;
  /** 화면에 보이는 불러오는 중 문장 — 주면 aria-label 대신 이 문장이 상태 영역의 내용이 된다 */
  caption?: string;
}

export function ListSkeleton({ count = 4, label = '목록 로딩 중', caption }: ListSkeletonProps) {
  return (
    <div
      role="status"
      aria-busy="true"
      aria-label={caption ? undefined : label}
      style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 24 }}
    >
      {Array.from({ length: count }, (_, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <SkeletonBlock width="40px" height="40px" borderRadius="50%" />
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <SkeletonLine width="60%" />
            <SkeletonLine width="40%" />
          </div>
        </div>
      ))}
      {caption ? <p className={s.caption}>{caption}</p> : null}
    </div>
  );
}

interface WindowSkeletonProps {
  label?: string;
}

export function WindowSkeleton({ label = '콘텐츠 로딩 중' }: WindowSkeletonProps = {}) {
  return <div className={s.windowSkeleton} role="status" aria-busy="true" aria-label={label} />;
}

interface PageSkeletonProps {
  variant: 'table' | 'list' | 'chat';
  label?: string;
  /** table variant 전용: 헤더/바디 열 수 (기본 4). 실제 테이블 컬럼 수와 맞춰 레이아웃 점프 방지. */
  cols?: number;
}

export function PageSkeleton({ variant, label, cols }: PageSkeletonProps) {
  switch (variant) {
    case 'table':
      return <TableSkeleton label={label} cols={cols} />;
    case 'list':
      return <ListSkeleton label={label} />;
    case 'chat':
      return (
        <div
          role="status"
          aria-busy="true"
          aria-label={label ?? '대화 로딩 중'}
          style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: 24 }}
        >
          {Array.from({ length: 5 }, (_, i) => (
            <div
              key={i}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 8,
                alignSelf: i % 2 === 0 ? 'flex-start' : 'flex-end',
                maxWidth: '70%',
              }}
            >
              {i % 2 === 0 && <SkeletonBlock width="32px" height="32px" borderRadius="50%" />}
              <SkeletonBlock width={`${120 + (i % 3) * 60}px`} height="36px" borderRadius="12px" />
            </div>
          ))}
        </div>
      );
  }
}
